import axios from "axios";

const DEFAULT_BRAND_LOGO = "/Assets/Images/color icon_circle.png";
export const APARTMENT_LOGO_CHANGED_EVENT = "adukkumadi:apartmentLogoChanged";
export const APARTMENT_LOGO_STORAGE_KEY = "apartmentBrandLogo";
export const APARTMENT_LOGO_ADMIN_KEY = "apartmentBrandLogoAdminId";

export function getLogoUrlFromRow(row) {
  return String(
    row?.logoViewUrl ||
      row?.logoUrl ||
      row?.imageUrl ||
      row?.url ||
      row?.image ||
      ""
  ).trim();
}

function pickLogoList(data) {
  if (!data || typeof data !== "object") return [];
  if (Array.isArray(data.logos)) return data.logos;
  if (Array.isArray(data.appartmentLogos)) return data.appartmentLogos;
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.result)) return data.result;
  if (Array.isArray(data.list)) return data.list;
  return [];
}

/**
 * Resolve adminId for logo listing: localStorage adminId, else userid.
 */
export function resolveApartmentLogoAdminId() {
  try {
    const adminId = String(localStorage.getItem("adminId") || "").trim();
    if (adminId) return adminId;
    return String(localStorage.getItem("userid") || "").trim();
  } catch {
    return "";
  }
}

/** Instant brand logo from cache (no network). */
export function readCachedApartmentLogoUrl() {
  try {
    const url = String(localStorage.getItem(APARTMENT_LOGO_STORAGE_KEY) || "").trim();
    if (!url) return "";

    const currentAdmin = resolveApartmentLogoAdminId();
    const cachedAdmin = String(localStorage.getItem(APARTMENT_LOGO_ADMIN_KEY) || "").trim();

    // Avoid showing another admin's cached logo after account switch
    if (currentAdmin && cachedAdmin && currentAdmin !== cachedAdmin) {
      return "";
    }
    return url;
  } catch {
    return "";
  }
}

export function writeCachedApartmentLogoUrl(url, adminId) {
  try {
    const clean = String(url || "").trim();
    const id = String(adminId || resolveApartmentLogoAdminId() || "").trim();
    if (clean) {
      localStorage.setItem(APARTMENT_LOGO_STORAGE_KEY, clean);
      if (id) localStorage.setItem(APARTMENT_LOGO_ADMIN_KEY, id);
    } else {
      localStorage.removeItem(APARTMENT_LOGO_STORAGE_KEY);
      localStorage.removeItem(APARTMENT_LOGO_ADMIN_KEY);
    }
  } catch {
    /* ignore */
  }
}

export function clearCachedApartmentLogoUrl() {
  writeCachedApartmentLogoUrl("");
}

export function getInitialBrandLogoUrl() {
  return readCachedApartmentLogoUrl() || DEFAULT_BRAND_LOGO;
}

/**
 * Fetch the first active apartment logo for sidebar brand.
 * Payload: { action: "appartmentLogo.getAll", adminId }
 * Also writes localStorage cache for instant next paint.
 */
export async function fetchActiveApartmentLogoUrl(adminId) {
  const API_BASE_URL = process.env.REACT_APP_API_URL;
  const id = String(adminId || resolveApartmentLogoAdminId() || "").trim();
  if (!API_BASE_URL || !id) return "";

  try {
    const response = await axios.post(`${API_BASE_URL}`, {
      action: "appartmentLogo.getAll",
      adminId: id
    });
    const data = response?.data;
    if (!(data?.status === "success" || data?.success === true)) {
      return readCachedApartmentLogoUrl();
    }
    const list = pickLogoList(data);
    if (!Array.isArray(list) || !list.length) {
      writeCachedApartmentLogoUrl("", id);
      return "";
    }

    // Prefer first active logo when API returns mixed list
    const active = list.find((row) => {
      if (typeof row?.isActive === "boolean") return row.isActive;
      return String(row?.status || "").toLowerCase() === "active";
    });
    const url = getLogoUrlFromRow(active || list[0]);
    writeCachedApartmentLogoUrl(url, id);
    return url;
  } catch {
    // Keep last known good logo on network errors
    return readCachedApartmentLogoUrl();
  }
}

/** Prefetch + cache brand logo (call after login before navigate). */
export async function prefetchApartmentBrandLogo(adminId) {
  const id = String(adminId || resolveApartmentLogoAdminId() || "").trim();
  if (!id) return getInitialBrandLogoUrl();
  const url = await fetchActiveApartmentLogoUrl(id);
  return url || DEFAULT_BRAND_LOGO;
}

export function notifyApartmentLogoChanged() {
  try {
    window.dispatchEvent(new CustomEvent(APARTMENT_LOGO_CHANGED_EVENT));
  } catch {
    /* ignore */
  }
}

export { DEFAULT_BRAND_LOGO };
